import React, { useState } from 'react';
import { Tooltip } from '../Tooltip';
import { SectionIntro } from '../SectionIntro';
import { CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
export const MarketProductFit = ({
  timeToValue,
  stickiness,
  valuePropBroadness,
  updateSimulation
}) => {
  const [feedback, setFeedback] = useState('');
  const handleTtvChange = (value: number) => {
    updateSimulation({
      timeToValue: value
    });
    if (value <= 2) {
      setFeedback('Excellent! Fast TTV (1-2) is critical for viral/self-service channels and drives strong word-of-mouth.');
    } else if (value === 3) {
      setFeedback('Good TTV for most B2B products. Consider ways to accelerate initial value delivery.');
    } else {
      setFeedback('Slow TTV may hurt retention. Users need to see value quickly to stick around.');
    }
  };
  return <div className="space-y-6" data-id="element-115">
      <SectionIntro title="Market ↔ Product Fit" description="This fit determines how well your product solves a real problem for your target market. It's about delivering value quickly and keeping users engaged long-term." whyItMatters="Without strong Market-Product Fit, everything else falls apart. You can't grow if users don't find value or don't stick around. This is your foundation." data-id="element-116" />
      <div className="space-y-2" data-id="element-117">
        <div className="flex justify-between items-center" data-id="element-118">
          <label className="flex items-center text-sm text-gray-300" data-id="element-119">
            Time To Value (TTV)
            <Tooltip title="Time To Value (TTV)" description="How quickly do customers experience their first 'aha moment'? Fast TTV (1-2) means users see value within minutes or hours. Slow TTV (4-5) means it takes days or weeks. The faster, the better for retention and word-of-mouth growth." learnMoreUrl="https://www.reforge.com/blog/how-to-build-a-growth-model" learnMoreText="Read Brian Balfour's framework" data-id="element-120" />
          </label>
          <span className="text-sm font-mono text-white" data-id="element-121">{timeToValue}/5</span>
        </div>
        <div className="relative" data-id="element-122">
          <input type="range" min="1" max="5" value={timeToValue} onChange={e => handleTtvChange(parseInt(e.target.value))} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer" data-id="element-123" />
          <div className="absolute inset-0 pointer-events-none" data-id="element-124">
            <div className="h-full bg-gradient-to-r from-green-500 to-blue-500 rounded-lg" style={{
            width: `${timeToValue / 5 * 100}%`
          }} data-id="element-125"></div>
          </div>
        </div>
        {feedback && <div className={`mt-2 p-2 rounded-lg flex items-start text-xs ${timeToValue <= 2 ? 'bg-green-500 bg-opacity-20 text-green-400' : timeToValue === 3 ? 'bg-blue-500 bg-opacity-20 text-blue-400' : 'bg-yellow-500 bg-opacity-20 text-yellow-400'}`} data-id="element-126">
            {timeToValue <= 2 ? <CheckCircleIcon className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" data-id="element-127" /> : <AlertCircleIcon className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" data-id="element-128" />}
            <span data-id="element-129">{feedback}</span>
          </div>}
      </div>
      <div className="space-y-2" data-id="element-130">
        <div className="flex justify-between items-center" data-id="element-131">
          <label className="flex items-center text-sm text-gray-300" data-id="element-132">
            Stickiness/Retention
            <Tooltip title="Stickiness/Retention" description="How often do users come back? High stickiness (4-5) means daily or weekly usage. Low stickiness (1-2) means users rarely return. Strong retention is the engine of sustainable growth—it's cheaper to keep users than acquire new ones." learnMoreUrl="https://www.reforge.com/blog/retention-engagement-growth-silent-killer" data-id="element-133" />
          </label>
          <span className="text-sm font-mono text-white" data-id="element-134">{stickiness}/5</span>
        </div>
        <div className="relative" data-id="element-135">
          <input type="range" min="1" max="5" value={stickiness} onChange={e => updateSimulation({
          stickiness: parseInt(e.target.value)
        })} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer" data-id="element-136" />
          <div className="absolute inset-0 pointer-events-none" data-id="element-137">
            <div className="h-full bg-gradient-to-r from-green-500 to-blue-500 rounded-lg" style={{
            width: `${stickiness / 5 * 100}%`
          }} data-id="element-138"></div>
          </div>
        </div>
        {stickiness >= 4 && <div className="mt-2 p-2 bg-green-500 bg-opacity-20 rounded-lg flex items-start text-xs text-green-400" data-id="element-139">
            <CheckCircleIcon className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" data-id="element-140" />
            <span data-id="element-141">
              Strong retention! This is your growth multiplier. Every user you
              acquire compounds over time.
            </span>
          </div>}
      </div>
      <div className="space-y-2" data-id="element-142">
        <label className="flex items-center text-sm text-gray-300" data-id="element-143">
          Value Prop Broadness
          <Tooltip title="Value Proposition Broadness" description="Does your product solve one specific problem (Niche) or many problems (Broad)? Niche products have stronger initial fit but smaller markets. Broad products have larger markets but may struggle with focus early on." learnMoreUrl="https://www.reforge.com/blog/how-to-build-a-growth-model" data-id="element-144" />
        </label>
        <div className="grid grid-cols-3 gap-2" data-id="element-145">
          {['Niche', 'Medium', 'Broad'].map(option => <button key={option} className={`py-2 px-4 rounded-md text-center text-sm transition ${valuePropBroadness === option ? 'bg-blue-500 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} onClick={() => updateSimulation({
          valuePropBroadness: option
        })} data-id="element-146">
              {option}
            </button>)}
        </div>
        <div className="mt-2 text-xs text-gray-400 italic" data-id="element-147">
          {valuePropBroadness === 'Niche' && '💡 Tip: Niche products often have the strongest early traction but may need to expand over time.'}
          {valuePropBroadness === 'Medium' && '💡 Tip: Medium breadth balances focus with market size—a sweet spot for many B2B products.'}
          {valuePropBroadness === 'Broad' && '💡 Tip: Broad products can capture large markets but may struggle to differentiate early on.'}
        </div>
      </div>
    </div>;
};